import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth, apiClient } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  X,
  Copy,
  Mail,
  MessageCircle,
  Facebook,
  Twitter,
  Linkedin,
  Send,
  Share2,
  Check,
  Smartphone,
  Gift,
  UserPlus,
  Star,
  DollarSign,
  Zap
} from "lucide-react";

const ShareModal = ({ isOpen, onClose, shareData }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [referralSettings, setReferralSettings] = useState({
    referrer_post_credits: 3,
    referee_discount_percent: 10,
    share_message: "Refer & get free credits"
  });
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  
  // Fetch referral settings from new API
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await apiClient.get("/referral/my-code");
        if (res.data) {
          setReferralSettings({
            referrer_post_credits: 3, // Default values until backend exposes details
            referee_discount_percent: 10,
            share_message: res.data.share_message || "Refer & get free credits"
          });
          setQrCodeUrl(res.data.qr_code_url);
        }
      } catch (err) {
        // Use defaults if fetch fails
      }
    };
    if (isOpen && user) {
      fetchSettings();
    }
  }, [isOpen, user]);
  
  // Use user's referral code if logged in, otherwise no referral code
  const referralCode = user?.referral_code || "";
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : "https://viraly.ca";
  // Referral URL goes directly to registration page with the referral code
  const referralUrl = referralCode ? `${baseUrl}/auth?ref=${referralCode}` : baseUrl;
  
  // Use provided shareData or generate default based on user state
  const defaultText = referralCode 
    ? `I'm using Viraly to create amazing social media content with AI! ${referralSettings.share_message}`
    : "Check out Viraly - AI-powered social media content creation! Start with 5 free posts!";
  
  const { title, text, url } = shareData || {
    title: "Viraly - AI Social Media Automation",
    text: defaultText,
    url: referralUrl
  };

  const fullText = `${text}\n\n${url}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  const shareOptions = [
    {
      name: "WhatsApp",
      icon: MessageCircle,
      color: "bg-green-600 hover:bg-green-700",
      action: () => {
        window.open(`https://wa.me/?text=${encodeURIComponent(fullText)}`, '_blank');
      }
    },
    {
      name: "SMS",
      icon: Smartphone,
      color: "bg-blue-500 hover:bg-blue-600",
      action: () => {
        // Works on mobile devices
        window.open(`sms:?body=${encodeURIComponent(fullText)}`, '_blank');
      }
    },
    {
      name: "Email",
      icon: Mail,
      color: "bg-purple-600 hover:bg-purple-700",
      action: () => {
        const subject = encodeURIComponent(title);
        const body = encodeURIComponent(fullText);
        window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
      }
    },
    {
      name: "Telegram",
      icon: Send,
      color: "bg-sky-500 hover:bg-sky-600",
      action: () => {
        window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
      }
    },
    {
      name: "Facebook",
      icon: Facebook,
      color: "bg-[#1877F2] hover:bg-[#166FE5]",
      action: () => {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`, '_blank', 'width=600,height=400');
      }
    },
    {
      name: "Twitter/X",
      icon: Twitter,
      color: "bg-black hover:bg-gray-800",
      action: () => {
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
      }
    },
    {
      name: "LinkedIn",
      icon: Linkedin,
      color: "bg-[#0A66C2] hover:bg-[#004182]",
      action: () => {
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
      }
    }
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-viraly-card rounded-2xl w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-viraly-gradient flex items-center justify-center">
                {user ? <Share2 className="w-5 h-5 text-white" /> : <Gift className="w-5 h-5 text-white" />}
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold">
                  {user ? "Share Viraly" : "Refer and Earn"}
                </h3>
                <p className="text-xs text-viraly-muted">
                  {user ? "Earn rewards for referrals!" : "Unlock exclusive benefits"}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {user ? (
              <>
                {/* Logged in user - Show share options with referral code */}
                {/* Message Preview */}
                <div className="mb-6 p-4 rounded-xl bg-viraly-dark border border-white/10">
                  <p className="text-sm text-viraly-light mb-2">{text}</p>
                  <p className="text-xs text-viraly-orange break-all">{url}</p>
                </div>

                {/* Share Options Grid */}
                <div className="grid grid-cols-4 gap-3 mb-6">
                  {shareOptions.map((option) => (
                    <button
                      key={option.name}
                      onClick={() => {
                        option.action();
                        toast.success(`Opening ${option.name}...`);
                      }}
                      className={`flex flex-col items-center gap-2 p-3 rounded-xl ${option.color} transition-all`}
                      data-testid={`share-${option.name.toLowerCase().replace('/', '-')}`}
                    >
                      <option.icon className="w-5 h-5 text-white" />
                      <span className="text-xs text-white font-medium">{option.name}</span>
                    </button>
                  ))}
                </div>

                {/* Copy Link Section */}
                <div>
                  <label className="block text-sm font-medium mb-2">Or copy link</label>
                  <div className="flex gap-2">
                    <Input
                      value={url}
                      readOnly
                      className="input-base flex-1 text-sm"
                    />
                    <Button
                      onClick={handleCopy}
                      className={`px-4 transition-all ${copied ? 'bg-viraly-success' : 'btn-primary'}`}
                    >
                      {copied ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Referral Info */}
                <div className="mt-6 p-4 rounded-xl bg-viraly-orange/10 border border-viraly-orange/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Gift className="w-4 h-4 text-viraly-orange" />
                    <p className="text-sm text-viraly-orange font-medium">
                      Your Referral Code: <span className="font-bold">{referralCode}</span>
                    </p>
                  </div>
                  <p className="text-xs text-viraly-muted">
                    {referralSettings.share_message}. When friends sign up and make their first purchase, you get free credits and they get {referralSettings.referee_discount_percent}% off!
                  </p>
                  
                  {/* QR Code Preview */}
                  {qrCodeUrl && (
                    <div className="mt-3 flex items-center gap-3">
                      <div className="w-16 h-16 bg-white rounded-lg p-1 flex-shrink-0">
                        <img src={qrCodeUrl} alt="Referral QR Code" className="w-full h-full" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-viraly-muted mb-2">
                          Scan or share this QR code for instant signup with your referral!
                        </p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              try {
                                const res = await fetch(qrCodeUrl);
                                const blob = await res.blob();
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `viraly-referral-${referralCode}.png`;
                                a.click();
                                URL.revokeObjectURL(url);
                                toast.success("QR Code downloaded!");
                              } catch {
                                toast.error("Failed to download QR code");
                              }
                            }}
                            className="text-xs"
                            data-testid="download-qr-btn"
                          >
                            <Copy className="w-3 h-3 mr-1" />
                            Download QR
                          </Button>
                          {/* Always show Share QR button - uses Web Share API if available, otherwise copies to clipboard */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              try {
                                if (navigator.share) {
                                  const res = await fetch(qrCodeUrl);
                                  const blob = await res.blob();
                                  const file = new File([blob], `viraly-referral-${referralCode}.png`, { type: 'image/png' });
                                  await navigator.share({
                                    title: 'Join Viraly with my referral!',
                                    text: `Use my code ${referralCode} to get 10% off!`,
                                    files: [file]
                                  });
                                } else {
                                  // Fallback: copy QR URL to clipboard
                                  await navigator.clipboard.writeText(qrCodeUrl);
                                  toast.success("QR code URL copied! You can paste it anywhere to share.");
                                }
                              } catch (err) {
                                if (err.name !== 'AbortError') {
                                  // Final fallback
                                  await navigator.clipboard.writeText(qrCodeUrl);
                                  toast.success("QR code URL copied!");
                                }
                              }
                            }}
                            className="text-xs"
                            data-testid="share-qr-btn"
                          >
                            <Share2 className="w-3 h-3 mr-1" />
                            Share QR
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-3 flex gap-2">
                    <div className="flex-1 px-3 py-2 rounded-lg bg-white/5 text-xs font-mono text-viraly-muted truncate">
                      {referralUrl}
                    </div>
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(referralUrl);
                        toast.success("Referral link copied!");
                      }}
                      size="sm"
                      className="btn-primary px-3"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Non-logged in user - Show benefits and encourage registration */}
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-viraly-orange to-purple-500 flex items-center justify-center mx-auto mb-4">
                    <UserPlus className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-2">
                    Sign Up to Unlock Referral Rewards
                  </h3>
                  <p className="text-viraly-muted text-sm">
                    Get your own unique referral code and start earning!
                  </p>
                </div>

                {/* Benefits Grid */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-viraly-dark border border-white/10">
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                      <Zap className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-viraly-light">5 Free Posts to Start</p>
                      <p className="text-xs text-viraly-muted">No credit card required</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-viraly-dark border border-white/10">
                    <div className="w-10 h-10 rounded-lg bg-viraly-orange/20 flex items-center justify-center flex-shrink-0">
                      <Gift className="w-5 h-5 text-viraly-orange" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-viraly-light">Earn Free Credits</p>
                      <p className="text-xs text-viraly-muted">Get rewarded when friends sign up & purchase</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-viraly-dark border border-white/10">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <Star className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-viraly-light">Loyalty Discounts</p>
                      <p className="text-xs text-viraly-muted">Save up to 25% with our loyalty program</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-viraly-dark border border-white/10">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <DollarSign className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-viraly-light">Become an Influencer</p>
                      <p className="text-xs text-viraly-muted">Earn 10% commission on referral purchases</p>
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                <Button 
                  onClick={() => {
                    onClose();
                    navigate("/auth");
                  }}
                  className="w-full btn-primary py-3 text-base"
                  data-testid="share-modal-signup-btn"
                >
                  <UserPlus className="w-5 h-5 mr-2" />
                  Sign Up Now - It's Free!
                </Button>
                
                <p className="text-center text-xs text-viraly-muted mt-3">
                  Already have an account?{" "}
                  <button 
                    onClick={() => {
                      onClose();
                      navigate("/auth");
                    }}
                    className="text-viraly-orange hover:underline"
                  >
                    Log in
                  </button>
                </p>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ShareModal;
